import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { X, Search, Loader2 } from "lucide-react";

const LEVEL_BADGE = {
  REGION: { label: "Region", cls: "bg-primary/10 text-primary" },
  PROVINCE: { label: "Province", cls: "bg-amber-100 text-amber-700" },
  CITY_MUNICIPALITY: { label: "City / Municipality", cls: "bg-emerald-50 text-emerald-700" },
};

/**
 * Searchable, mobile-first multi-select for PSGC Service Areas.
 * value: array of {code, level, name}. Prevents exact-duplicate codes.
 */
export const ServiceAreaPicker = ({ value = [], onChange, testid = "service-area-picker" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/search`, { params: { q: q.trim(), limit: 12 } });
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 280); // debounce
    return () => clearTimeout(timer.current);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const add = (r) => {
    if (value.some((v) => v.code === r.code)) { setQ(""); setResults([]); setOpen(false); return; }
    onChange([...value, { code: r.code, level: r.level, name: r.name }]);
    setQ(""); setResults([]); setOpen(false);
  };
  const remove = (code) => onChange(value.filter((v) => v.code !== code));

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <div className="relative mt-1.5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid={`${testid}-input`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search a region, province, city or municipality…"
          className="w-full h-11 pl-9 pr-9 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      </div>

      {open && results.length > 0 && (
        <ul data-testid={`${testid}-results`} className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-border bg-white shadow-lg">
          {results.map((r) => {
            const b = LEVEL_BADGE[r.level] || {};
            const picked = value.some((v) => v.code === r.code);
            return (
              <li key={r.code}>
                <button type="button" disabled={picked} onClick={() => add(r)}
                  data-testid={`sa-option-${r.code}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${picked ? "opacity-40 cursor-not-allowed" : ""}`}>
                  <span className="truncate">{r.name}{r.parentName ? <span className="text-slate-400 font-normal">, {r.parentName}</span> : null}</span>
                  <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full ${b.cls || "bg-slate-100 text-slate-500"}`}>{b.label || r.level}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2.5" data-testid={`${testid}-chips`}>
          {value.map((v) => {
            const b = LEVEL_BADGE[v.level] || {};
            return (
              <span key={v.code} data-testid={`sa-chip-${v.code}`}
                className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full pl-3 pr-1.5 py-1 text-sm">
                <span className="font-medium text-slate-700">{v.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${b.cls || "bg-white text-slate-500"}`}>{b.label || v.level}</span>
                <button type="button" onClick={() => remove(v.code)} data-testid={`sa-remove-${v.code}`}
                  className="ml-0.5 h-6 w-6 grid place-items-center rounded-full hover:bg-slate-200 text-slate-500" aria-label={`Remove ${v.name}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
